import { Box, Flex, Text, Button, Skeleton } from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { cacheManagementServices } from 'services'
import { CacheStatisticsResponse } from 'services/cacheManagementServices'
import { AxiosError } from 'axios'

interface StatisticsOverviewProps {
  onRefresh?: () => void
}

/**
 * Statistics Overview Component
 * Displays cache statistics cards
 */
export const StatisticsOverview: FC<StatisticsOverviewProps> = ({
  onRefresh,
}) => {
  const [loading, setLoading] = useState(true)
  const [statistics, setStatistics] = useState<CacheStatisticsResponse | null>(
    null,
  )
  const [refreshing, setRefreshing] = useState(false)

  const fetchStatistics = async () => {
    try {
      setRefreshing(true)
      const response = await cacheManagementServices.getStatistics()
      if (response.data) {
        setStatistics(response.data)
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error('Error fetching statistics:', error)
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchStatistics()
  }, [])

  const handleRefresh = async () => {
    await fetchStatistics()
    onRefresh?.()
  }

  const calculateCoverage = (cached: number, total: number) => {
    if (total === 0) return 0
    return Math.round((cached / total) * 100)
  }

  const summaryCoverage = statistics
    ? calculateCoverage(statistics.summary_cache, statistics.news_with_reports)
    : 0
  const wordcloudCoverage = statistics
    ? calculateCoverage(
        statistics.wordcloud_cache,
        statistics.news_with_reports,
      )
    : 0

  return (
    <Box bgColor="white.50" borderRadius={4} mb="16px" padding="24px" w="100%">
      <Flex align="center" justifyContent="space-between" mb="24px">
        <Text
          fontSize="24px"
          fontWeight="700"
          letterSpacing="tighter"
          lineHeight="40px"
        >
          キャッシュ統計
        </Text>
        <Button
          isLoading={refreshing}
          size="sm"
          variant="outline"
          onClick={handleRefresh}
        >
          🔄 更新
        </Button>
      </Flex>

      {loading ? (
        <Flex gap="24px">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} borderRadius="12px" flex={1} height="120px" />
          ))}
        </Flex>
      ) : statistics ? (
        <Flex gap="24px">
          {/* Summary Cache Card */}
          <Box
            bgColor="white.100"
            border="1px solid #E8ECEF"
            borderRadius="12px"
            flex={1}
            padding="24px"
          >
            <Text color="black.100" fontSize="14px" fontWeight="600" mb="8px">
              Summary Cache
            </Text>
            <Text color="blue.900" fontSize="32px" fontWeight="700" mb="4px">
              {statistics.summary_cache.toLocaleString()}
            </Text>
            <Text color="black.100" fontSize="12px" opacity={0.6}>
              {summaryCoverage}% coverage
            </Text>
          </Box>

          {/* WordCloud Cache Card */}
          <Box
            bgColor="white.100"
            border="1px solid #E8ECEF"
            borderRadius="12px"
            flex={1}
            padding="24px"
          >
            <Text color="black.100" fontSize="14px" fontWeight="600" mb="8px">
              WordCloud Cache
            </Text>
            <Text color="blue.900" fontSize="32px" fontWeight="700" mb="4px">
              {statistics.wordcloud_cache.toLocaleString()}
            </Text>
            <Text color="black.100" fontSize="12px" opacity={0.6}>
              {wordcloudCoverage}% coverage
            </Text>
          </Box>

          {/* News with Reports Card */}
          <Box
            bgColor="white.100"
            border="1px solid #E8ECEF"
            borderRadius="12px"
            flex={1}
            padding="24px"
          >
            <Text color="black.100" fontSize="14px" fontWeight="600" mb="8px">
              News with Reports
            </Text>
            <Text color="blue.900" fontSize="32px" fontWeight="700" mb="4px">
              {statistics.news_with_reports.toLocaleString()}
            </Text>
            <Text color="black.100" fontSize="12px" opacity={0.6}>
              Total available
            </Text>
          </Box>
        </Flex>
      ) : null}
    </Box>
  )
}
